import { useLayoutEffect } from 'react';
import { usePrevious } from 'react-use';

import { visitPaths } from '~libs.desktop.shared.paths';

import { VisitEncounterMode, VisitEncounterModeFilterParam } from './VisitEncounterModeField.types';
import {
   FILTER_SEPARATOR,
   getVisitEncounterModeFromSearchParam,
   getVisitEncounterModesFilterSearchParamValue,
   mapVisitEncounterModeFilterParamToEncounterMode,
} from './VisitEncounterModeField.utils';

const VISIT_ENCOUNTER_MODE_FILTER_STORAGE_KEY = 'visitEncounterModeFilter';

type SetFilter = (filter: VisitEncounterModeFilterParam) => void;
type DeleteFilter = VoidFunction;
type GetStoredEncounterModes = () => VisitEncounterMode[];

type VisitEncounterModeFilterStorageReturnFn = {
   setFilter: SetFilter;
   deleteFilter: DeleteFilter;
   getStoredEncounterModes: GetStoredEncounterModes;
};

export const visitEncounterModeFilterStorage: VisitEncounterModeFilterStorageReturnFn = (() => {
   const deleteFilter: DeleteFilter = () => window.sessionStorage.removeItem(VISIT_ENCOUNTER_MODE_FILTER_STORAGE_KEY);
   const setFilter: SetFilter = (filter) => {
      if (filter === '') return deleteFilter();

      return window.sessionStorage.setItem(VISIT_ENCOUNTER_MODE_FILTER_STORAGE_KEY, filter);
   };

   const getStoredEncounterModes: GetStoredEncounterModes = () =>
      mapVisitEncounterModeFilterParamToEncounterMode(
         window.sessionStorage.getItem(VISIT_ENCOUNTER_MODE_FILTER_STORAGE_KEY)
      );

   return {
      setFilter: setFilter,
      getStoredEncounterModes: getStoredEncounterModes,
      deleteFilter: deleteFilter,
   };
})();

export const useSyncVisitEncounterModeFilterParamWithStorage = (
   searchParams: URLSearchParams,
   onSync: (searchParams: URLSearchParams) => void
): void => {
   const visitEncounterModeFilterParam = searchParams.get(visitPaths.VISIT_ENCOUNTER_MODE_SEARCH_PARAM);
   const previousVisitEncounterModeFilterParam = usePrevious(visitEncounterModeFilterParam);
   const memoizedVisitEncounterModeFilter = visitEncounterModeFilterStorage.getStoredEncounterModes();

   useLayoutEffect(() => {
      if (visitEncounterModeFilterParam && visitEncounterModeFilterParam !== previousVisitEncounterModeFilterParam) {
         const visitEncounterModesFromSearchParam = visitEncounterModeFilterParam.split(FILTER_SEPARATOR).map(Number);
         const visitEncounterModesValid = visitEncounterModesFromSearchParam.every((id) => !isNaN(id));

         if (!visitEncounterModesValid) {
            searchParams.delete(visitPaths.VISIT_ENCOUNTER_MODE_SEARCH_PARAM);
            onSync(searchParams);
         }

         if (visitEncounterModesValid) {
            visitEncounterModeFilterStorage.setFilter(visitEncounterModeFilterParam);
         }
      }

      if (memoizedVisitEncounterModeFilter.length) {
         const visitEncounterModesFromSearchParam = visitEncounterModeFilterParam?.split(FILTER_SEPARATOR).map(Number);
         const memoizedModesAsSet = new Set(memoizedVisitEncounterModeFilter);

         if (
            !visitEncounterModesFromSearchParam ||
            visitEncounterModesFromSearchParam.some((searchParamValue) => {
               const modeFromSearchParam = getVisitEncounterModeFromSearchParam(searchParamValue);
               return !modeFromSearchParam || !memoizedModesAsSet.has(modeFromSearchParam);
            })
         ) {
            const newVisitEncounterModeFilterParam = getVisitEncounterModesFilterSearchParamValue(
               memoizedVisitEncounterModeFilter
            );
            searchParams.set(visitPaths.VISIT_ENCOUNTER_MODE_SEARCH_PARAM, newVisitEncounterModeFilterParam);
            onSync(searchParams);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [visitEncounterModeFilterParam, memoizedVisitEncounterModeFilter]);
};
